import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { capitalize, defaultTo, get } from 'lodash';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { TrackingHandler } from 'client/tracking/handler';
import { CommonMap } from 'client/tracking/maps/common';
import { venomHistory } from 'client/utils/history/venom-history';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildSubmodelsPath, buildVehiclePath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { ALL_CAT_NAME, PHOTO_TYPE } from 'site-modules/shared/constants/photoflipper/photoflipper';
import { PICTURES_HEADER_SRC } from 'site-modules/shared/constants/pictures/pictures-header';
import { isUsed } from 'site-modules/shared/utils/publication-states';
import { getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { getPicturesPageTitle } from 'site-modules/pictures/utils/pictures-seo-utils';
import { getQuery } from 'client/utils/location';
import { MoreAboutMMYLink } from 'site-modules/shared/components/more-about-mmy-link/more-about-mmy-link';
import { CoreBreadcrumbs } from 'site-modules/shared/components/core-breadcrumbs/core-breadcrumbs';
import { StyledSubmodelDropdown } from 'site-modules/shared/components/vehicle-dropdowns/submodel-dropdown';
import { CategorySelector } from 'site-modules/pictures/components/category-selector/category-selector';
import { Gallery } from 'site-modules/pictures/components/gallery/gallery';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';

import './pictures.scss';

export const PAGE_DISPLAY_NAME = 'Pictures';
export const URL_PATH = 'pictures';

const GALLERY_HEADING_CLASSES = 'heading-5 mt-1_5 mt-md-1 mb-0';

function PicturesUI({ vehicle, params, submodels, location, isMobile }) {
  const { make, model, year } = params;
  const { submodel } = getQuery(location);
  const category = defaultTo(params.category, ALL_CAT_NAME);
  const search = get(location, 'search', '');
  const pathname = get(location, 'pathname', '');

  useEffect(() => {
    TrackingHandler.useMap(CommonMap);
  }, []);

  const setSubmodel = useCallback(
    newSubmodel => {
      const submodelName = get(newSubmodel, 'name');

      venomHistory.push({
        search: submodelName ? `?submodel=${submodelName}` : '',
        pathname,
      });
    },
    [pathname]
  );

  const setCategory = useCallback(
    newCategory => {
      const basePathname = `/${make}/${model}/${year}/pictures/`;

      venomHistory.push(
        newCategory === ALL_CAT_NAME ? `${basePathname}${search}` : `${basePathname}${newCategory}/${search}`
      );
    },
    [make, model, search, year]
  );

  if (!vehicle) {
    return (
      <Container tag="main" id="main-content" className="pictures">
        Loading&hellip;
      </Container>
    );
  }

  const displayVehicle = buildDisplayVehicle({ makeModelSubmodelYear: vehicle, params, usedPrefix: '' });
  const extendedParams = { ...params, submodel, category, mediaType: PHOTO_TYPE };

  const categoryLabel = capitalize(category);
  const pageTitle = getPicturesPageTitle({ displayVehicle, category, isMeta: false });

  return (
    <main id="main-content" className="pictures pos-r" data-tracking-parent="pictures">
      <div
        style={{
          backgroundImage: `url('${PICTURES_HEADER_SRC[isMobile]}')`,
          backgroundSize: '100% 100%',
        }}
      >
        <Container className="mb-1 pb-2 pb-md-3_5">
          <CoreBreadcrumbs
            className="ps-0 text-white mb-1_5 mb-md-2"
            vehicle={vehicle}
            breadcrumbsParams={{
              params,
              pageDisplayName: PAGE_DISPLAY_NAME,
              urlPath: getUrlPathFromParams(params, URL_PATH),
            }}
          />
          <Row>
            <Col xs={12} lg={9}>
              <h1
                className={classnames('text-white fw-bold', {
                  'size-36': !isMobile,
                  'size-24': isMobile,
                })}
              >
                {pageTitle}
              </h1>
              <div>
                <MoreAboutMMYLink
                  vehicle={vehicle}
                  className="text-white"
                  linkClassName={null}
                  iconClassName={null}
                  dataTrackingId="click_go_to_core"
                  dataTrackingValue="more_about"
                  linkToReview={isUsed(vehicle.pubStates)}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <Container>
        <Row className="mb-1_5">
          <Col xs={12} md={6} lg={4}>
            <StyledSubmodelDropdown
              {...vehicle}
              pubStates={Object.keys(vehicle.pubStates)}
              value={submodel}
              onChange={setSubmodel}
              labelText="Select Type"
              labelClassName="fw-bold size-16 text-gray-darker"
              toggle="All Types"
              id="pictures-select-submodel"
              isFirstOptionDisabled={false}
            />
          </Col>
        </Row>
        <CategorySelector
          params={extendedParams}
          selectedCategory={category}
          setCategory={setCategory}
          search={search}
          isMobile={isMobile}
        />
        <h2
          className={classnames(GALLERY_HEADING_CLASSES, { 'd-none': submodel, 'visible-gallery-heading': !submodel })}
        >
          {categoryLabel} {displayVehicle} Pictures
        </h2>
        {submodels.map(({ name }) => (
          <h2
            className={classnames(GALLERY_HEADING_CLASSES, {
              'd-none': name !== submodel,
              'visible-gallery-heading': name === submodel,
            })}
            key={name}
          >
            {categoryLabel} {year} {vehicle.make.name} {name} Pictures
          </h2>
        ))}
        <Gallery params={extendedParams} category={category} />
        <PageLinksWidget divideSections={false} title={`Related ${displayVehicle} info`} />
      </Container>
    </main>
  );
}

PicturesUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  submodels: PropTypes.arrayOf(PropTypes.shape()),
  params: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.string,
  }),
  location: PropTypes.shape({}),
  isMobile: PropTypes.bool,
};

PicturesUI.defaultProps = {
  vehicle: null,
  submodels: [],
  params: {},
  location: {},
  isMobile: false,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const Pictures = connect(mapStateToProps)(
  connectToModel(PicturesUI, {
    submodels: bindToPath(({ params }) => buildSubmodelsPath(params), VehicleModel),
    vehicle: bindToPath(({ params }) => buildVehiclePath(params), VehicleModel),
  })
);
